import React, { useState } from 'react';
import { SupportTicketPriority, TTicketData } from 'views/types/tickets';
import Card from 'components/card';
import dayjs from 'dayjs';
import * as Dialog from '@radix-ui/react-dialog';
import { IoIosCloseCircle } from 'react-icons/io';
import { ticketsService } from 'views/services/ticketsService';
import { EditTicketForm } from '../EditTicketForm';
import { AssignAdminDialog } from '../AssignAdminDialog';
import { EscalateDialog } from '../EscalateDialog';
import { ScheduleDialog } from '../ScheduleDialog';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  ticket: TTicketData | null;
};

type TAction =
  | 'delete'
  | 'close'
  | 'edit'
  | 'assign'
  | 'escalade'
  | 'schedule'
  | 'resolve';

export const TicketDetailsDialog: React.FC<Props> = ({
  open,
  setOpen,
  ticket,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<null | TAction>(null);
  const { canUpdate, canDelete } = usePermissions();

  if (!ticket) return null;

  const handleAction = (type: TAction) => {
    setActionType(type);
    if (['delete', 'close', 'resolve'].includes(type)) {
      setConfirmOpen(true);
    }
  };

  const confirmAction = async () => {
    if (actionType && ticket) {
      if (actionType === 'close') {
        await ticketsService.closeTicket(ticket.id);
      } else if (actionType === 'delete') {
        await ticketsService.deleteTicket(ticket.id);
      } else if (actionType === 'resolve') {
        await ticketsService.resolveTicket(ticket.id);
      }
    }
    setConfirmOpen(false);
    setActionType(null);
    setOpen(false);
  };

  const onClose = () => {
    setActionType(null);
    setOpen(false);
    setConfirmOpen(false);
  };

  return (
    <>
      {!!actionType && actionType === 'edit' ? (
        <EditTicketForm open={open} setOpen={onClose} ticket={ticket} />
      ) : !!actionType && actionType === 'assign' ? (
        <AssignAdminDialog open={open} setOpen={onClose} ticketId={ticket.id} />
      ) : !!actionType && actionType === 'escalade' ? (
        <EscalateDialog open={open} setOpen={onClose} ticketId={ticket.id} />
      ) : !!actionType && actionType === 'schedule' ? (
        <ScheduleDialog open={open} setOpen={onClose} ticketId={ticket.id} />
      ) : (
        <>
          <Dialog.Root open={open} onOpenChange={onClose}>
            <Dialog.Portal>
              <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" />
              <Dialog.Content className="data-[state=open]:animate-contentShow fixed top-1/2 left-1/2 z-50 h-[80vh] w-full max-w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-navy-800 dark:text-white p-6 shadow-xl focus:outline-none">
                <div className="absolute top-2 right-2">
                  <Dialog.Close asChild>
                    <button className="text-gray-500 border-none hover:text-gray-700">
                      <IoIosCloseCircle size={24} />
                    </button>
                  </Dialog.Close>
                </div>

                <Dialog.Title className="text-lg font-bold mb-4">
                  Ticket #{ticket.id}
                </Dialog.Title>
                <Card className="space-y-2">
                  <p className="flex justify-between">
                    <strong>Client ID:</strong>{' '}
                    <span>{ticket.user?.coins_fly_id}</span>
                  </p>
                  <p className="flex justify-between items-start">
                    <strong className="min-w-[80px]">Message:</strong>
                    <span
                      className="max-h-[200px] overflow-y-auto break-words text-left px-2"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {ticket.message}
                    </span>
                  </p>
                  {ticket.adminNotes && (
                    <p className="flex justify-between">
                      <strong>Admin Notes:</strong>
                      <span>{ticket.adminNotes}</span>
                    </p>
                  )}
                  <p className="flex justify-between">
                    <strong>Status:</strong> <span>{ticket.status}</span>
                  </p>
                  <p className="flex justify-between">
                    <strong>Category:</strong> <span>{ticket.category}</span>
                  </p>
                  <p className="flex justify-between">
                    <strong>Priority:</strong>
                    <span>
                      {ticket.priority === SupportTicketPriority.NOT_URGENT
                        ? 'Not Urgent'
                        : 'Urgent'}
                    </span>
                  </p>
                  {/*{ticket.isEscalated && (
                    <p className="flex justify-between">
                      <strong>Escalated Reason:</strong>
                      <span>{ticket.escalationReason}</span>
                    </p>
                  )}*/}
                  {ticket.assignedAdminId && (
                    <p className="flex justify-between">
                      <strong>Assigned to Admin ID:</strong>
                      <span>{ticket.assignedAdminId}</span>
                    </p>
                  )}
                  {ticket.scheduledContactTime && (
                    <p className="flex justify-between">
                      <strong>Scheduled To:</strong>
                      <span>
                        {dayjs(ticket.scheduledContactTime).format(
                          'DD.MM.YYYY'
                        )}
                      </span>
                    </p>
                  )}
                  {ticket.closedAt && (
                    <p className="flex justify-between">
                      <strong>Closed At:</strong>
                      <span>{dayjs(ticket.closedAt).format('DD.MM.YYYY')}</span>
                    </p>
                  )}
                  {ticket.resolvedAt && (
                    <p className="flex justify-between">
                      <strong>Resolved At:</strong>
                      <span>
                        {dayjs(ticket.resolvedAt).format('DD.MM.YYYY')}
                      </span>
                    </p>
                  )}
                  <p className="flex justify-between">
                    <strong>Created At:</strong>
                    <span>
                      {dayjs(ticket.createdAt).format('YYYY-MM-DD HH:mm')}
                    </span>
                  </p>
                  {ticket.metadata && (
                    <div>
                      <strong>Metadata:</strong>
                      <pre className="text-sm bg-gray-100 dark:bg-gray-600 p-2 rounded mt-1">
                        {JSON.stringify(ticket.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </Card>

                <div className="mt-6 flex gap-2">
                  {!ticket.assignedAdminId && (
                    <button
                      onClick={() => handleAction('assign')}
                      className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        !canUpdate(PERMISSION_RESOURCES.SUPPORT_TICKETS) ||
                        !canUpdate(PERMISSION_RESOURCES.TICKET_INNER_MENU)
                      }
                    >
                      Assign
                    </button>
                  )}

                  {/*{!ticket.isEscalated && (
                    <button
                      onClick={() => handleAction('escalade')}
                      className="px-4 py-2 bg-blue-500 text-white rounded"
                    >
                      Escalade
                    </button>
                  )}*/}
                  {!ticket.scheduledContactTime && (
                    <button
                      onClick={() => handleAction('schedule')}
                      className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        !canUpdate(PERMISSION_RESOURCES.SUPPORT_TICKETS) ||
                        !canUpdate(PERMISSION_RESOURCES.TICKET_INNER_MENU)
                      }
                    >
                      Schedule
                    </button>
                  )}
                  <button
                    onClick={() => handleAction('edit')}
                    className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                      !canUpdate(PERMISSION_RESOURCES.SUPPORT_TICKETS) ||
                      !canUpdate(PERMISSION_RESOURCES.TICKET_INNER_MENU)
                    }
                  >
                    Edit
                  </button>
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => handleAction('delete')}
                    className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                      !canDelete(PERMISSION_RESOURCES.SUPPORT_TICKETS) ||
                      !canDelete(PERMISSION_RESOURCES.TICKET_INNER_MENU)
                    }
                  >
                    Delete
                  </button>
                  {!ticket.closedAt && (
                    <button
                      onClick={() => handleAction('close')}
                      className="px-4 py-2 bg-yellow-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        !canUpdate(PERMISSION_RESOURCES.SUPPORT_TICKETS) ||
                        !canUpdate(PERMISSION_RESOURCES.TICKET_INNER_MENU)
                      }
                    >
                      Close
                    </button>
                  )}
                  {!ticket.resolvedAt && (
                    <button
                      onClick={() => handleAction('resolve')}
                      className="px-4 py-2 bg-green-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        !canUpdate(PERMISSION_RESOURCES.SUPPORT_TICKETS) ||
                        !canUpdate(PERMISSION_RESOURCES.TICKET_INNER_MENU)
                      }
                    >
                      Resolve
                    </button>
                  )}
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" />
              <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-navy-800 dark:text-white p-6 shadow-xl focus:outline-none">
                <Dialog.Title className="text-lg font-bold mb-4">
                  {actionType === 'delete'
                    ? 'Delete'
                    : actionType === 'resolve'
                      ? 'Resolve'
                      : 'Close'}{' '}
                  Ticket
                </Dialog.Title>
                <p className="mb-4">
                  Are you sure you want to {actionType} ticket #{ticket.id}?
                </p>
                <div className="flex justify-end gap-2">
                  <Dialog.Close asChild>
                    <button className="px-4 py-2 bg-gray-300 rounded">
                      Cancel
                    </button>
                  </Dialog.Close>
                  <button
                    onClick={confirmAction}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Confirm
                  </button>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </>
      )}
    </>
  );
};
